import {
  BillingEntity,
  ICreateInvoice,
  IInvoice,
} from "@shared/lib/types/briefCampains";
import styles from "./styles.module.scss";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Col, Flex, Modal, Row, Tag } from "antd";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useCreateInvoice } from "@entities/campaigns/lib/hooks/useCreateInvoice";
import { useEditInvoice } from "@entities/campaigns/lib/hooks/useEditInvoice";
import { ToastService } from "@shared/lib/services";
import { InvoiceEntryForm } from "./InvoiceEntryForm";
import { useDeleteInvoice } from "@entities/campaigns/lib/hooks";
import { Button } from "@shared/ui/Button";

export function InvoiceDetails({
  invoices,
  campaignInfluencerId,
  billing,
}: {
  invoices: IInvoice[];
  campaignInfluencerId: string;
  billing?: BillingEntity;
}) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<IInvoice | null>(null);

  const handleEditClick = (invoice: IInvoice) => {
    setSelectedInvoice(invoice);
    setIsEditModalOpen(true);
  };
  const { createInvoice, isCreating, isCreated } =
    useCreateInvoice(campaignInfluencerId);
  const { editInvoice, isEditing, isEdited } =
    useEditInvoice(campaignInfluencerId);

  const { deleteInvoice, isDeleting, isDeleted, variables } =
    useDeleteInvoice(campaignInfluencerId);

  useEffect(() => {
    if (isCreated || isEdited) {
      ToastService.success("Invoice created successfully");
      setIsEditModalOpen(false);
      setSelectedInvoice(null);
    }
  }, [isCreated, isEdited]);

  useEffect(() => {
    if (isDeleted) {
      ToastService.success("Invoice deleted successfully");
    }
  }, [isDeleted]);

  const handleSubmit = (values: ICreateInvoice) => {
    if (selectedInvoice) {
      editInvoice({
        ...values,
        invoiceId: selectedInvoice._id,
        campaignInfluencerId,
      });
    } else {
      createInvoice({ ...values, campaignInfluencerId });
    }
  };

  return (
    <div className={styles.ledgerCard}>
      <div className={styles.ledgerHeader}>
        <span className={styles.ledgerTitleMain}>Invoices</span>
        <button
          className={styles.ledgerAddBtn}
          onClick={() => {
            setIsEditModalOpen(true);
            setSelectedInvoice(null);
          }}
        >
          <PlusOutlined style={{ marginRight: 4 }} /> Add Invoice
        </button>
      </div>
      <div className={styles.ledgerList}>
        {invoices.map((inv) => {
          const isCreditNote = inv.type === "Credit-Note";
          const amount = (
            inv.baseCost +
            (inv.gstAmount || 0) -
            (inv.tdsAmount || 0)
          ).toFixed(2);

          const breakdown = [
            inv.baseCost !== undefined ? `+ Base Cost: ₹${inv.baseCost}` : null,
            inv.gstAmount
              ? `+ GST (${inv.gstPercentage}%): ₹${inv.gstAmount}`
              : null,
            inv.tdsAmount
              ? `- TDS (${inv.tdsPercentage}%): ₹${inv.tdsAmount}`
              : null,
          ]
            .filter(Boolean)
            .join("\n");
          return (
            <Row key={inv._id}>
              <Col span={8}>
                <div className={styles.ledgerLeft} style={{ width: "100%" }}>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <Tag color={isCreditNote ? "green" : "blue"}>
                      {isCreditNote ? "Credit Note" : "Invoice"}
                    </Tag>
                    {/* Edit icon if editable */}

                    <EditOutlined
                      className={styles.ledgerEditIcon}
                      onClick={() => handleEditClick(inv)}
                    />

                    <span
                      style={{ color: "#888", fontWeight: 500, marginLeft: 8 }}
                    >
                      ({format(new Date(), "dd MMM yyyy")})
                    </span>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <a
                      href={inv.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1890ff" }}
                    >
                      View
                    </a>
                    <span style={{ color: "#888", marginLeft: 16 }}>
                      Uploaded by {inv.createdBy?.firstName || "Admin"}
                    </span>
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <div
                  style={{
                    flex: 1,
                    color: "#222",
                    fontWeight: 600,
                    fontSize: 18,
                    textAlign: "center",
                  }}
                >
                  {inv.remarks}
                  <div
                    style={{
                      color: "#888",
                      fontWeight: 400,
                      fontSize: 14,
                      marginTop: 2,
                    }}
                  >
                    Invoice Date{" "}
                    {format(new Date(inv.invoiceDate), "dd MMM yyyy")}
                  </div>
                </div>
              </Col>
              <Col span={8}>
                <Flex justify="end" gap={8}>
                  <div style={{ textAlign: "right" }}>
                    <div className={styles.ledgerAmount}>₹{amount}</div>
                    {breakdown && (
                      <div className={styles.ledgerBreakdown}>
                        {breakdown.split("\n").map((line, i) => (
                          <div key={i}>{line}</div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button
                    danger
                    loading={isDeleting && variables?.invoiceId === inv._id}
                    onClick={() => deleteInvoice({ invoiceId: inv._id })}
                    icon={<DeleteOutlined />}
                  />
                </Flex>
              </Col>
            </Row>
          );
        })}
      </div>
      <Modal
        open={isEditModalOpen}
        onCancel={() => setIsEditModalOpen(false)}
        footer={null}
        destroyOnClose
        title={selectedInvoice ? `Edit Invoice` : "Add Invoice"}
      >
        <InvoiceEntryForm
          initialValues={
            selectedInvoice
              ? {
                  type: selectedInvoice.type,
                  baseCost: selectedInvoice.baseCost,
                  gstPercentage: selectedInvoice.gstPercentage,
                  tdsPercentage: selectedInvoice.tdsPercentage,
                  hasGst: selectedInvoice.isGst,
                  hasTds: selectedInvoice.isTds,
                  remarks: selectedInvoice.remarks,
                  campaignInfluencerId: selectedInvoice.campaignInfluencerId,
                  url: selectedInvoice.url,
                  invoiceDate: selectedInvoice.invoiceDate,
                }
              : { tdsPercentage: billing?.tdsPercentage || 10 }
          }
          onSubmit={handleSubmit}
          isSubmitting={isCreating || isEditing}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
